import React, { useState } from 'react'
import PropTypes from 'prop-types'
import 'focus-visible'
import classnames from 'classnames'

import { ArrowRoundedRight, Calendar, Clock, Location } from '@aok/icon-functional'

import { eventProptypes } from '../../../utils/propTypes/event'
import { getDistanceById } from '../../../utils/LocalisationUtils'
import { OfferSearchTileTypes } from '../../../utils/typeDefinitions/offerSearch'
import { useKeyboardFocusHelper } from '../../../hooks/customHooks'

import { OfferSearchDateTime, OfferSearchState, OfferSearchAddress, OfferSearchDistance } from '../../atoms/OfferSearch'
import OfferSearchBookmarkButton from '../../atoms/OfferSearch/OfferSearchBookmarkButton'
import OfferSearchQuantityDuration from '../../atoms/OfferSearch/OfferSearchQuantityDuration'
import { getEventDurationAndQuantity } from '../../../utils/OfferSearchUtils'
import { EVENT_ONLINE_TYPE } from '../../../utils/constants/event'

const OfferSearchTile = ({ event, metaData, onClick, className, isBookmarked, setBookmarks, variant, testId }) => {
  const stateContentClasses = classnames({
    //search offer
    '600:flex-row 600:justify-between 600:gap-x-24': variant === OfferSearchTileTypes.OFFER_SEARCH,
    // macro
    '840:flex-row 840:justify-between 840:gap-x-24': variant === OfferSearchTileTypes.MACRO,
  })

  const distance = getDistanceById(event?.id, metaData?.distances)
  const showFullAddress = !!metaData?.search_mode?.additional_fields?.find((field) => field === 'fullAddress')
  const showLocationName = !!metaData?.search_mode?.additional_fields?.find((field) => field === 'locationName')

  const [titleLinkIsFocused, setTitleLinkIsFocused] = useState(false)

  const isKeyboard = useKeyboardFocusHelper()

  const [eventQuantity, duration] = getEventDurationAndQuantity(event)

  const courseFormat =
    event.online_type === EVENT_ONLINE_TYPE.ONLINE
      ? 'Online'
      : event.online_type === EVENT_ONLINE_TYPE.HYBRID
      ? 'Kurs vor Ort und Online'
      : 'Kurs vor Ort'

  return (
    <div className="relative" data-cy={testId}>
      <div
        className={`
        offer-search-tile
        bg-ffffff group cursor-pointer
        ${titleLinkIsFocused ? 'shadow-greenOutline2' : ''}
        ${className}
      `}
        onClick={() => onClick(event.id, event.client.id)}
        role="presentation"
      >
        <div className={OfferSearchTileTypes.OFFER_SEARCH === variant ? 'mr-56' : ''}>
          <div className="flex flex-row">
            {/* Training format */}
            <p aria-label={`Kursformat: ${courseFormat}`} className="text-6d767c">
              {courseFormat}
            </p>

            {/* Training type */}
            {event.training_types?.length > 0 && (
              <>
                <span className="mx-6 text-6d767c"> | </span>
                <p aria-label={`Kurstyp: ${event.training_types[0].name}`} className="text-6d767c">
                  {event.training_types[0].name}
                </p>
              </>
            )}
          </div>

          {/* Event title */}
          <button
            aria-label={`Titel des Kurses: ${event.training_title}`}
            data-cy="result-details-toggle"
            className="mt-8 group flex items-center text-left"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onClick(event.id, event.client.id)
            }}
            onFocus={() => {
              if (isKeyboard) {
                setTitleLinkIsFocused(true)
              }
            }}
            onBlur={() => {
              setTitleLinkIsFocused(false)
            }}
          >
            <span
              aria-hidden={true}
              className="font-semibold font-buenos-aires text-20 leading-28 text-005e3f group-hover:text-18ab42 group-focus-visible:text-18ab42 inline-block"
            >
              {event.training_title}
              <ArrowRoundedRight className="inline-block ml-8 relative bottom-2 w-16 h-16 fill-current transition-transform duration-350 ease-in-out group-hover:scale-120" />
            </span>
          </button>
          {/* Location name */}
          {showLocationName && !!event.location?.name && <p className="mt-8 text-6d767c">{event.location.name}</p>}
        </div>

        <div className={`flex flex-col gap-y-8 ${showLocationName && !!event.location?.name ? 'mt-16' : 'mt-24'}`}>
          {/* Location */}
          {!metaData?.search_mode?.hide_fields?.includes('location') &&
            event.online_type !== EVENT_ONLINE_TYPE.ONLINE && (
              <div className="flex flex-wrap items-center">
                {(!!distance || event?.location?.address) && (
                  <Location className="mr-8 relative bottom-2 w-16 h-16 fill-current text-6d767c" />
                )}

                {/* Distance */}
                {!!distance && <OfferSearchDistance distance={distance} />}

                {/* Address */}
                {event?.location?.address && (
                  <OfferSearchAddress location={event.location} showFullAddress={showFullAddress} listView />
                )}
              </div>
            )}

          {/* Date */}
          {!!event.date_string && (
            <div className="flex items-center">
              <Calendar className="relative bottom-2 mr-8 w-16 h-16 fill-current text-6d767c flex-shrink-0" />
              <OfferSearchDateTime dateString={event.date_string} className="font-semibold" />
            </div>
          )}

          {/* Time */}
          <div className={`flex flex-wrap gap-y-16 flex-col 1000:gap-x-32 ${stateContentClasses}`}>
            <div className="flex items-center">
              {(!!event.time_string || !!eventQuantity) && (
                <Clock className="relative bottom-2 mr-8 w-16 h-16 fill-current text-6d767c flex-shrink-0" />
              )}
              {!!event.time_string && (
                <>
                  <div className="flex ">
                    <OfferSearchDateTime timeString={event.time_string} className="font-semibold" />
                  </div>
                  {!!eventQuantity && <span className="mx-6">|</span>}
                </>
              )}
              {!!eventQuantity && <OfferSearchQuantityDuration eventQuantity={eventQuantity} duration={duration} />}
            </div>
            <div className="flex items-center">
              {/* Availability state */}
              {!!event.state && !!event.state_title && (
                <OfferSearchState
                  state={event.state}
                  stateTitle={event.state_title}
                  className="1280:ml-auto 1280:order-last text-293033"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bookmark button */}
      {OfferSearchTileTypes.OFFER_SEARCH === variant && (
        <OfferSearchBookmarkButton
          id={event.id}
          testId="course-bookmark-button"
          title={event.training_title}
          isBookmarked={isBookmarked}
          onClick={setBookmarks}
          className="absolute top-24 600:top-32 right-0"
        />
      )}
    </div>
  )
}

OfferSearchTile.propTypes = {
  metaData: PropTypes.shape({
    // if distances is filled, it's an object where the keys are the ids of the events
    // if no distance for an event is available, it's an empty array
    // if a distance is available, it's an object with a distance property
    distances: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.objectOf(
        PropTypes.oneOfType([
          PropTypes.shape({
            distance: PropTypes.string,
          }),
          PropTypes.array,
        ])
      ),
    ]),
    search_mode: PropTypes.object,
  }),
  event: PropTypes.shape(eventProptypes),
  onClick: PropTypes.func,
  className: PropTypes.string,
  isBookmarked: PropTypes.bool,
  setBookmarks: PropTypes.func,
  variant: PropTypes.string,
  testId: PropTypes.string,
}

OfferSearchTile.defaultProps = {
  event: {
    title: 'Kein Titel definiert',
    training_types: [
      {
        title: 'Kein Trainingstyp definiert',
      },
    ],
  },
  onClick: () => {},
  className: '',
  isBookmarked: false,
  variant: OfferSearchTileTypes.MACRO,
}

export default OfferSearchTile
